import { canonicalEquationFormula, canonicalEquationVariables } from "./equationSpecs.js";

export const equationLifecycleStates = ["DRAFT", "CANDIDATE", "EXPERIMENTAL", "RESTRICTED", "DEPRECATED", "RETIRED"];
export const equationVersionPattern = /^\d+\.\d+\.\d+$/;

function withGovernanceDefaults(equation) {
  return {
    introducedAt: "2026-06-19",
    version: "1.0.0",
    lifecycleState: "CANDIDATE",
    ...equation
  };
}

export const equationCatalog = [
  {
    id: "master-strategic-viability",
    label: "Master Strategic Viability",
    formula: canonicalEquationFormula("master-strategic-viability"),
    status: ["DRAFT", "OPERATOR_MODEL", "NEEDS_EXPERIMENT"],
    anchors: [
      "CLEARY_AND_ANCIENT_CORPUS_MATH_AUDIT.md:247",
      "pg-sunzi-giles-1910",
      "pg-prince-machiavelli-marriott-1908",
      "pg-meditations-marcus-aurelius",
      "pg-leviathan-hobbes-1651"
    ],
    variables: canonicalEquationVariables("master-strategic-viability"),
    blockedClaims: ["not formal proof", "not corpus-count verified across Cleary", "not legal or financial advice"],
    featureTargets: ["Decision Receipt Ledger", "Equation Workbench"]
  },
  {
    id: "shih-configuration-force",
    label: "Shih Configuration Force",
    formula: canonicalEquationFormula("shih-configuration-force"),
    status: ["SOURCE_ANCHORED", "OPERATOR_MODEL", "NEEDS_EXPERIMENT"],
    anchors: ["VALIDATION_SOURCES.md:50", "ctext-sunzi", "pg-sunzi-giles-1910", "pg-on-war-clausewitz-graham-1874"],
    variables: canonicalEquationVariables("shih-configuration-force"),
    blockedClaims: ["not mystical force", "not guaranteed victory"],
    featureTargets: ["Shih Simulator", "Terrain / Position Map"]
  },
  {
    id: "no-battle-victory",
    label: "No-Battle Victory",
    formula: canonicalEquationFormula("no-battle-victory"),
    status: ["SOURCE_ANCHORED", "OPERATOR_MODEL", "NEEDS_EXPERIMENT"],
    anchors: ["pg-sunzi-giles-1910", "shambhala-cleary-strategy-guide"],
    variables: canonicalEquationVariables("no-battle-victory"),
    blockedClaims: ["not pacifism in all cases", "not proof without scenario data"],
    featureTargets: ["No-Battle Victory Planner"]
  },
  {
    id: "timing-window",
    label: "Timing Window",
    formula: canonicalEquationFormula("timing-window"),
    status: ["SOURCE_ANCHORED", "OPERATOR_MODEL", "NEEDS_EXPERIMENT"],
    anchors: ["pg-sunzi-giles-1910", "CLEARY_AND_ANCIENT_CORPUS_MATH_AUDIT.md:302"],
    variables: canonicalEquationVariables("timing-window"),
    blockedClaims: ["requires current facts", "timing cannot be inferred from text alone"],
    featureTargets: ["Timing Window Planner"]
  },
  {
    id: "terrain-position-leverage",
    label: "Terrain / Position Leverage",
    formula: canonicalEquationFormula("terrain-position-leverage"),
    status: ["SOURCE_ANCHORED", "OPERATOR_MODEL", "NEEDS_EXPERIMENT"],
    anchors: ["pg-sunzi-giles-1910", "pg-on-war-clausewitz-graham-1874", "ctext-sunzi"],
    variables: canonicalEquationVariables("terrain-position-leverage"),
    blockedClaims: ["requires domain-specific terrain map"],
    featureTargets: ["Terrain / Position Map"]
  },
  {
    id: "information-asymmetry",
    label: "Information Asymmetry",
    formula: canonicalEquationFormula("information-asymmetry"),
    status: ["SOURCE_ANCHORED", "OPERATOR_MODEL", "NEEDS_EXPERIMENT"],
    anchors: ["pg-sunzi-giles-1910", "pg-on-war-clausewitz-graham-1874"],
    variables: canonicalEquationVariables("information-asymmetry"),
    blockedClaims: ["does not endorse unlawful deception", "requires lawful/safety gate"],
    featureTargets: ["Influence Planner", "Decision Receipt Ledger"]
  },
  {
    id: "calculations-quality",
    label: "Calculations Quality",
    formula: canonicalEquationFormula("calculations-quality"),
    status: ["SOURCE_ANCHORED", "OPERATOR_MODEL", "NEEDS_EXPERIMENT"],
    anchors: ["EQUATION_REGISTRY.md:36", "pg-sunzi-giles-1910", "pg-on-war-clausewitz-graham-1874"],
    variables: canonicalEquationVariables("calculations-quality"),
    blockedClaims: ["does not reward raw iteration count"],
    featureTargets: ["Equation Workbench", "Decision Receipt Ledger"]
  },
  {
    id: "guiguzi-influence-fit",
    label: "Guiguzi Influence Fit",
    formula: canonicalEquationFormula("guiguzi-influence-fit"),
    status: ["SOURCE_ANCHORED", "OPERATOR_MODEL", "NEEDS_SOURCE_TERMS_REVIEW"],
    anchors: ["ctext-guiguzi", "pg-prince-machiavelli-marriott-1908"],
    variables: canonicalEquationVariables("guiguzi-influence-fit"),
    blockedClaims: ["not manipulation engine", "must include ethics/lawful gate"],
    featureTargets: ["Influence Planner"]
  },
  {
    id: "i-ching-change-kernel",
    label: "I Ching Change Kernel",
    formula: canonicalEquationFormula("i-ching-change-kernel"),
    status: ["SOURCE_ANCHORED", "OPERATOR_MODEL", "NEEDS_SOURCE_TERMS_REVIEW"],
    anchors: ["ctext-i-ching", "shambhala-cleary-i-ching-guide"],
    variables: canonicalEquationVariables("i-ching-change-kernel"),
    blockedClaims: ["not supernatural oracle", "not prediction without evidence"],
    featureTargets: ["Timing Window Planner", "Attention / Clarity Monitor"]
  },
  {
    id: "musashi-yagyu-execution-clarity",
    label: "Musashi/Yagyu Execution Clarity",
    formula: canonicalEquationFormula("musashi-yagyu-execution-clarity"),
    status: ["SOURCE_ANCHORED", "OPERATOR_MODEL", "LICENSED_SOURCE_NEEDED"],
    anchors: ["shambhala-cleary-strategy-guide"],
    variables: canonicalEquationVariables("musashi-yagyu-execution-clarity"),
    blockedClaims: ["Cleary text not counted", "requires licensed text for corpus claims"],
    featureTargets: ["Attention / Clarity Monitor", "Timing Window Planner"]
  },
  {
    id: "zen-attention-kernel",
    label: "Zen/Buddhist Attention Kernel",
    formula: canonicalEquationFormula("zen-attention-kernel"),
    status: ["SOURCE_ANCHORED", "OPERATOR_MODEL", "SOURCE_REVIEW_NEEDED"],
    anchors: ["CLEARY_AND_ANCIENT_CORPUS_MATH_AUDIT.md:326", "pg-meditations-marcus-aurelius"],
    variables: canonicalEquationVariables("zen-attention-kernel"),
    blockedClaims: ["not spiritual attainment proof", "not medical guidance"],
    featureTargets: ["Attention / Clarity Monitor"]
  },
  {
    id: "governance-fa-standards",
    label: "Governance / Fa Standards",
    formula: canonicalEquationFormula("governance-fa-standards"),
    status: ["SOURCE_ANCHORED", "OPERATOR_MODEL", "NEEDS_EXPERIMENT"],
    anchors: [
      "VALIDATION_SOURCES.md:89",
      "ctext-seven-military-classics",
      "pg-bushido-nitobe-1908",
      "pg-prince-machiavelli-marriott-1908",
      "pg-leviathan-hobbes-1651"
    ],
    variables: canonicalEquationVariables("governance-fa-standards"),
    blockedClaims: ["not endorsement of authoritarian politics", "requires ethics gate"],
    featureTargets: ["Governance Audit", "Decision Receipt Ledger"]
  }
].map((equation) => {
  const lifecycleById = {
    "calculations-quality": "EXPERIMENTAL",
    "zen-attention-kernel": "EXPERIMENTAL",
    "i-ching-change-kernel": "RESTRICTED",
    "musashi-yagyu-execution-clarity": "RESTRICTED"
  };

  return withGovernanceDefaults({
    ...equation,
    lifecycleState: lifecycleById[equation.id] ?? "CANDIDATE"
  });
});

export function equationSummary(catalog = equationCatalog) {
  return catalog.reduce(
    (summary, equation) => {
      summary.total += 1;
      for (const status of equation.status) {
        summary.statuses[status] = (summary.statuses[status] ?? 0) + 1;
      }

      summary.lifecycleStates[equation.lifecycleState] = (summary.lifecycleStates[equation.lifecycleState] ?? 0) + 1;
      summary.versionCount[equation.version] = (summary.versionCount[equation.version] ?? 0) + 1;
      return summary;
    },
    { total: 0, statuses: {}, lifecycleStates: {}, versionCount: {} }
  );
}
